import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  MessageSquare, 
  Database, 
  History, 
  Users, 
  LogOut,
  Sparkles,
  Settings,
  BookOpen,
  Shield,
  Download,
  PieChart,
  Calendar,
  PanelLeftClose,
  PanelLeft,
  ChevronLeft,
  ChevronRight,
  Brain
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import Logo from './Logo';

const Sidebar = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Base nav items for all users
  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/my-dashboard', icon: PieChart, label: 'My Dashboard', badge: 'NEW' },
    { to: '/query', icon: MessageSquare, label: 'AI Query', badge: 'AI' },
    { to: '/learning', icon: BookOpen, label: 'Learning' },
    { to: '/history', icon: History, label: 'Query History' },
    { to: '/scheduled-reports', icon: Calendar, label: 'Scheduled Reports' },
    { to: '/downloads', icon: Download, label: 'Downloads' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  // Admin-only items (including connections)
  const adminItems = [
    { to: '/connections', icon: Database, label: 'Connections' },
    { to: '/lookup-tables', icon: Sparkles, label: 'AI Lookups' },
    { to: '/learnings-inspector', icon: Brain, label: 'Learnings Inspector' },
    { to: '/users', icon: Users, label: 'User Management' },
    { to: '/security', icon: Shield, label: 'Security' },
  ];

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className={`flex flex-col h-full ${collapsed ? 'w-16' : 'w-64'} bg-white/80 backdrop-blur-sm border-r border-[#D4AF37]/20 transition-all duration-300`}>
      {/* Logo & Collapse Toggle */}
      <div className={`p-4 flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && <Logo size="small" />}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-gray-500" />
          )}
        </button>
      </div>

      <Separator className="bg-[#D4AF37]/10" />

      {/* Navigation */}
      <ScrollArea className="flex-1 px-2 py-4">
        <nav className="space-y-1">
          <TooltipProvider delayDuration={0}>
            {navItems.map((item) => (
              <Tooltip key={item.to}>
                <TooltipTrigger asChild>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `sidebar-link flex items-center gap-3 ${collapsed ? 'justify-center px-2' : 'px-3'} py-2.5 rounded-lg text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-[#D4AF37]/15 text-[#B8962E] border-l-2 border-[#D4AF37]'
                          : 'text-gray-600 hover:text-[#B8962E] hover:bg-[#D4AF37]/5'
                      }`
                    }
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="truncate">{item.label}</span>
                        {item.badge && (
                          <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-white">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </TooltipTrigger>
                {collapsed && (
                  <TooltipContent side="right" className="flex items-center gap-2">
                    <span>{item.label}</span>
                    {item.badge && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-white">
                        {item.badge}
                      </span>
                    )}
                  </TooltipContent>
                )}
              </Tooltip>
            ))}

            {isAdmin && (
              <>
                {!collapsed && (
                  <div className="pt-4 pb-2">
                    <p className="px-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Admin
                    </p>
                  </div>
                )}
                {collapsed && <Separator className="my-2 bg-[#D4AF37]/10" />}
                {adminItems.map((item) => (
                  <Tooltip key={item.to}>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={item.to}
                        className={({ isActive }) =>
                          `sidebar-link flex items-center gap-3 ${collapsed ? 'justify-center px-2' : 'px-3'} py-2.5 rounded-lg text-sm font-medium transition-all ${
                            isActive
                              ? 'bg-[#D4AF37]/15 text-[#B8962E] border-l-2 border-[#D4AF37]'
                              : 'text-gray-600 hover:text-[#B8962E] hover:bg-[#D4AF37]/5'
                          }`
                        }
                        data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                      >
                        <item.icon className="w-4 h-4 flex-shrink-0" />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                      </NavLink>
                    </TooltipTrigger>
                    {collapsed && (
                      <TooltipContent side="right">
                        <span>{item.label}</span>
                      </TooltipContent>
                    )}
                  </Tooltip>
                ))}
              </>
            )}
          </TooltipProvider>
        </nav>

        {/* Quick Tips Card - only when expanded */}
        {!collapsed && (
          <div className="mt-6 mx-1 p-4 rounded-xl bg-gradient-to-br from-[#D4AF37]/10 to-[#F4E5A3]/20 border border-[#D4AF37]/20">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs font-semibold text-[#B8962E]">Pro Tip</span>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Ask questions like "Show me deposit trends for the last week" in the AI Query section.
            </p>
          </div>
        )}
      </ScrollArea>

      <Separator className="bg-[#D4AF37]/10" />

      {/* User Profile */}
      <div className={`p-3 ${collapsed ? 'flex flex-col items-center' : ''}`}>
        <TooltipProvider delayDuration={0}>
          {collapsed ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Avatar className="w-9 h-9 border border-[#D4AF37]/30 cursor-pointer">
                    <AvatarFallback className="bg-gradient-to-br from-[#D4AF37]/20 to-[#F4E5A3]/30 text-[#B8962E] text-sm font-medium">
                      {user?.name ? getInitials(user.name) : 'U'}
                    </AvatarFallback>
                  </Avatar>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <div>
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                    <p className="text-xs text-[#B8962E] mt-1">{user?.role}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleLogout}
                    className="mt-2 text-gray-500 hover:text-red-600 hover:bg-red-50"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Sign out</p>
                </TooltipContent>
              </Tooltip>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="w-9 h-9 border border-[#D4AF37]/30">
                  <AvatarFallback className="bg-gradient-to-br from-[#D4AF37]/20 to-[#F4E5A3]/30 text-[#B8962E] text-sm font-medium">
                    {user?.name ? getInitials(user.name) : 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                  user?.role === 'admin' 
                    ? 'bg-[#D4AF37]/20 text-[#B8962E]' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {user?.role}
                </span>
              </div>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="w-full justify-start text-gray-500 hover:text-red-600 hover:bg-red-50"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Sign out of your account</p>
                </TooltipContent>
              </Tooltip>
            </>
          )}
        </TooltipProvider>
      </div>
    </div>
  );
};

export default Sidebar;
